import { faCamera, faSearch } from '@fortawesome/pro-light-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography, useMediaQuery } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { FieldError, Option } from '@spike/model';
import clsx from 'clsx';
import { DescriptionField2, NumberField2, SelectField, TextField2 } from 'components/UI';
import ButtonV3 from 'components/UI/V3/Button/Button';
import { useMasterData } from 'hooks';
import clone from 'lodash/clone';
import set from 'lodash/set';
import { Product, WholeProduct, createBusinessProduct } from '@spike/product-model';
import { FunctionComponent, useState } from 'react';
import ProductCategoryComponent from './ProductCategoryComponent';
import LookupProduct from './UI/LookupProduct';
import isEmpty from 'lodash/isEmpty';
import { getUnit } from '../../Utils';
import { v4 as uuid } from 'uuid';
import ImageUpload from 'components/UI/ImageUpload';

interface Props {
    product: Product;
    showLookup?: boolean;
    errors: Array<FieldError>;
    onChange?: (product: Product) => void;
    onAddCategory: () => void;
    onLookup: () => void;
    onNext: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            [theme.breakpoints.down(768)]: {
                flexDirection: 'column',
                gap: '16px',
                paddingRight: 0,
                paddingLeft: 0,
                minHeight: 0
            },
            [theme.breakpoints.between(768, 'md')]: {
                paddingRight: '18px',
                paddingLeft: '37px',
                minHeight: '480px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingRight: '22px',
                paddingLeft: '45px',
                minHeight: '600px'
            }
        },
        leftContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '20%',
            flexShrink: 0,
            [theme.breakpoints.down(768)]: {
                width: '100%',
                alignItems: 'flex-start'
            }
        },
        rightContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '80%',
            minWidth: 0,
            [theme.breakpoints.down(768)]: {
                width: '100%'
            }
        },
        nameContainer: {
            display: 'flex',
            width: '100%',
            [theme.breakpoints.down(768)]: {
                marginBottom: '16px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                marginBottom: '39px'
            },
            [theme.breakpoints.up('lg')]: {
                marginBottom: '48px'
            }
        },
        descriptionContainer: {
            display: 'flex',
            width: '100%'
        },
        barcodeCategoryContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '50%',
            minWidth: 0,
            [theme.breakpoints.down(768)]: {
                width: '100%'
            }
        },
        description: {
            '& .MuiOutlinedInput-multiline': {
                [theme.breakpoints.down('md')]: {
                    height: '137px'
                },
                [theme.breakpoints.up('lg')]: {
                    height: '173px'
                }
            }
        },
        imageBox: {
            [theme.breakpoints.down('md')]: {
                width: '118px',
                height: '118px',
                borderRadius: '8px'
            },
            [theme.breakpoints.up('lg')]: {
                width: '147px',
                height: '147px',
                borderRadius: '10px'
            }
        },
        uploadBox: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            backgroundColor: '#E9F0ED',
            [theme.breakpoints.down('md')]: {
                borderRadius: '8px'
            },
            [theme.breakpoints.up('lg')]: {
                borderRadius: '10px'
            }
        },
        uploadIcon: {
            color: '#5E8677',
            [theme.breakpoints.down('md')]: {
                fontSize: '38px',
                lineHeight: '24px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '47px',
                lineHeight: '30px'
            }
        },
        titleSection: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            width: '100%',
            gap: '12px',
            marginBottom: '30px',
            [theme.breakpoints.down(768)]: {
                flexDirection: 'column',
                marginBottom: '16px',
                gap: '8px'
            }
        },
        informationSection: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        },
        title: {
            fontWeight: 600,
            [theme.breakpoints.down('md')]: {
                fontSize: '15px',
                lineHeight: '19px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '18px',
                lineHeight: '24px'
            }
        },
        lookup: {
            cursor: 'pointer',
            fontWeight: 500,
            color: '#EAB464',
            [theme.breakpoints.down('md')]: {
                fontSize: '13px',
                lineHeight: '19px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px',
                lineHeight: '24px'
            }
        },
        iconSearch: {
            [theme.breakpoints.down('md')]: {
                marginRight: '3px',
                marginTop: '3px'
            },
            [theme.breakpoints.up('lg')]: {
                marginRight: '4px',
                marginTop: '4px'
            }
        },
        lookupRow: {
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            [theme.breakpoints.down(768)]: {
                marginBottom: 0
            }
        },
        horizontalContainer: {
            display: 'flex',
            [theme.breakpoints.down(768)]: {
                flexDirection: 'column',
                gap: '16px',
                marginBottom: '16px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                marginBottom: '39px'
            },
            [theme.breakpoints.up('lg')]: {
                marginBottom: '48px'
            }
        },
        paddingRight: {
            [theme.breakpoints.down(768)]: {
                paddingRight: 0
            },
            [theme.breakpoints.between(768, 'md')]: {
                paddingRight: '16px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingRight: '20px'
            }
        },
        iconInfo: {
            color: '#5E8677',
            [theme.breakpoints.down('md')]: {
                marginTop: '2px'
            },
            [theme.breakpoints.up('lg')]: {
                marginTop: '3px'
            }
        },
        messageContainer: {
            display: 'flex',
            position: 'absolute',
            background: '#ffffff',
            zIndex: 300,
            border: '1px solid #000',
            [theme.breakpoints.down(768)]: {
                borderRadius: '4px',
                padding: '9px 11px',
                marginLeft: 0,
                marginTop: '24px',
                left: '16px',
                right: '16px',
                maxWidth: 'calc(100vw - 64px)'
            },
            [theme.breakpoints.between(768, 'md')]: {
                borderRadius: '4px',
                padding: '9px 11px',
                marginLeft: '112px',
                marginTop: '-40px'
            },
            [theme.breakpoints.up('lg')]: {
                borderRadius: '6px',
                padding: '12px 14px',
                marginLeft: '140px',
                marginTop: '-50px'
            }
        },
        message: {
            [theme.breakpoints.down('md')]: {
                fontSize: '11px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '14px'
            }
        },
        titleManufacturer: {
            fontWeight: 500
        },
        manufacturerTitleContainer: {
            display: 'flex',
            position: 'relative'
        },
        pricingContainer: {
            [theme.breakpoints.down('md')]: {
                marginTop: '37px',
                marginBottom: '39px'
            },
            [theme.breakpoints.up('lg')]: {
                marginTop: '45px',
                marginBottom: '48px'
            }
        },
        marginNone: {
            marginBottom: '0px !important'
        },
        checkout: {
            [theme.breakpoints.down('md')]: {
                fontSize: '13px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px'
            },
            '& .MuiSwitch-root': {
                [theme.breakpoints.down('md')]: {
                    width: '30px',
                    height: '16px',
                    borderRadius: '16px'
                },
                [theme.breakpoints.up('lg')]: {
                    width: '36px',
                    height: '20px',
                    borderRadius: '20px'
                }
            },
            '& .MuiSwitch-thumb': {
                [theme.breakpoints.down('md')]: {
                    width: '13px',
                    height: '12px'
                },
                [theme.breakpoints.up('lg')]: {
                    width: '17px',
                    height: '16px'
                }
            }
        },
        checkoutPadding: {
            '&.MuiSwitch-track': {
                backgroundColor: '#5E8677'
            },
            [theme.breakpoints.down('md')]: {
                paddingRight: '5px',
                paddingLeft: '11px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingRight: '7px',
                paddingLeft: '14px'
            }
        },
        buttonContainer: {
            display: 'flex',
            flexDirection: 'row-reverse',
            width: '100%',
            [theme.breakpoints.down('md')]: {
                marginBottom: '39px'
            },
            [theme.breakpoints.up('lg')]: {
                marginBottom: '48px'
            }
        },
        amount: {
            [theme.breakpoints.down('md')]: {
                fontSize: '13px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px'
            }
        },
        iconAsterisk: {
            color: '#EAB464',
            [theme.breakpoints.down('md')]: {
                marginRight: '6px'
            },
            [theme.breakpoints.up('lg')]: {
                marginRight: '8px'
            }
        },
        actions: {
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            [theme.breakpoints.down(768)]: {
                'marginTop': '16px',
                'marginBottom': '8px',
                '& > button': {
                    width: '100%'
                }
            },
            [theme.breakpoints.between(768, 'md')]: {
                marginTop: '24px',
                marginBottom: '39px'
            },
            [theme.breakpoints.up('lg')]: {
                marginTop: '32px',
                marginBottom: '48px'
            }
        }
    })
);

export const Details: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const masterData = useMasterData();

    const isMobile = useMediaQuery('(max-width:767.95px)');
    const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.only('md'));
    const isNetbook = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

    const [hover, setHover] = useState(false);

    const [showLookup, setShowLookup] = useState(false);

    const [productImage, setProductImage] = useState(props.product.imageUrl);

    const unitOptions: Array<Option<number>> = masterData.productUnits.map(unit => ({
        id: unit.id,
        name: unit.name,
        description: unit.unit
    }));

    const shouldHideAmount = (unit?: Option<number> | null) => {
        if (!unit?.id) {
            return false;
        }

        if (unit.id === WholeProduct.unit?.id) {
            return true;
        }

        const productUnit = masterData.productUnits.find(item => item.id === unit.id);
        return productUnit?.unit === 'u';
    };

    const changeHandler = (value: any, field: string) => {
        const updatedProduct = clone(props.product);
        set(updatedProduct.businessProduct!, field, value);

        if (updatedProduct.createdFromScratch) {
            set(updatedProduct, field, value);
        }

        if (field === 'size.unit' && shouldHideAmount(value)) {
            updatedProduct.businessProduct!.size!.amount = undefined;
        }

        props.onChange && props.onChange(updatedProduct);
    };

    const imageChangeHandler = (
        image: string,
        imageFileToUpload: File | undefined,
        imageRemoved: boolean | undefined
    ) => {
        setProductImage(image);
        const updatedProduct = clone(props.product);

        updatedProduct.businessProduct!.imageUrl = image;
        updatedProduct.businessProduct!.imageFile = imageFileToUpload;
        updatedProduct.businessProduct!.deleteImage = imageRemoved;
        props.onChange && props.onChange(updatedProduct);
    };

    const selectProductHandler = (product: Product) => {
        const newProduct = {
            ...product,
            createdFromScratch: false,
            businessProduct: createBusinessProduct(uuid, product)
        };
        setProductImage(product.imageUrl);
        props.onChange && props.onChange(newProduct);
        setShowLookup(false);
    };

    const manufacturerHeader = (
        <Box className={classes.manufacturerTitleContainer}>
            <Typography className={classes.titleManufacturer}>Manufacturer</Typography>
            <Typography className={classes.iconAsterisk}>*</Typography>
            <FontAwesomeIcon
                className={classes.iconInfo}
                icon={faInfoCircle}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
            />
            {hover && (
                <Box className={classes.messageContainer}>
                    <Typography className={classes.message}>
                        The name of the business or
                        <br /> company which makes goods
                        <br /> in large quantities to sell. May
                        <br /> be the same or different name
                        <br /> from the brand itself.
                    </Typography>
                </Box>
            )}
        </Box>
    );

    return (
        <Box>
            <Box className={classes.container}>
                <Box className={classes.leftContainer}>
                    <ImageUpload
                        squared={true}
                        small={isMobile || isTablet || isNetbook}
                        imageUrl={productImage}
                        onChangeImage={imageChangeHandler}
                    ></ImageUpload>
                </Box>
                <Box className={classes.rightContainer}>
                    <Box className={classes.titleSection}>
                        <Typography className={classes.title}>Primary Information</Typography>
                        {props.showLookup && (
                            <Box
                                className={classes.lookupRow}
                                onClick={() => setShowLookup(true)}
                            >
                                <FontAwesomeIcon
                                    icon={faSearch}
                                    className={clsx(classes.lookup, classes.iconSearch)}
                                />
                                <Typography className={classes.lookup}>Lookup product</Typography>
                            </Box>
                        )}
                    </Box>
                    {!showLookup && (
                        <Box className={classes.informationSection}>
                            <Box className={classes.nameContainer}>
                                <TextField2
                                    label="Product Name"
                                    name="name"
                                    value={
                                        isEmpty(props.product.businessProduct?.name)
                                            ? props.product.name
                                            : props.product.businessProduct?.name || ''
                                    }
                                    placeholder="Product Name"
                                    required={true}
                                    onChange={changeHandler}
                                    capitalize={true}
                                    errors={props.errors}
                                />
                            </Box>
                            <Box className={classes.horizontalContainer}>
                                <Box className={clsx(classes.barcodeCategoryContainer, classes.paddingRight)}>
                                    <TextField2
                                        label="Barcode"
                                        name="code"
                                        value={props.product?.businessProduct?.code || ''}
                                        placeholder="Barcode"
                                        onChange={changeHandler}
                                        errors={props.errors}
                                    />
                                </Box>
                                <Box className={classes.barcodeCategoryContainer}>
                                    <ProductCategoryComponent
                                        categorySelected={props.product.category}
                                        onChange={category => changeHandler(category, 'category')}
                                        onAdd={props.onAddCategory}
                                        error={props.errors.some(error => error.fieldName === 'category')}
                                        disabled={
                                            props.product.id !== undefined && props.product.category !== undefined
                                        }
                                    />
                                </Box>
                            </Box>
                            <Box className={classes.horizontalContainer}>
                                <Box className={clsx(classes.paddingRight, classes.barcodeCategoryContainer)}>
                                    <TextField2
                                        label={manufacturerHeader}
                                        name="manufacturer"
                                        value={
                                            isEmpty(props.product.businessProduct?.manufacturer)
                                                ? props.product.manufacturer
                                                : props.product.businessProduct?.manufacturer || ''
                                        }
                                        placeholder="Manufacturer"
                                        onChange={changeHandler}
                                        capitalize={true}
                                        errors={props.errors}
                                    />
                                </Box>
                                <Box className={classes.barcodeCategoryContainer}>
                                    <TextField2
                                        label="Brand"
                                        name="brand"
                                        value={
                                            isEmpty(props.product.businessProduct?.brand)
                                                ? props.product.brand
                                                : props.product.businessProduct?.brand || ''
                                        }
                                        placeholder="Brand"
                                        onChange={changeHandler}
                                        capitalize={true}
                                        required={true}
                                        errors={props.errors}
                                    />
                                </Box>
                            </Box>
                            <Box className={classes.horizontalContainer}>
                                <Box className={clsx(classes.paddingRight, classes.barcodeCategoryContainer)}>
                                    <SelectField
                                        label="Unit Measurement"
                                        name="size.unit"
                                        placeholder="Select Measurement"
                                        options={unitOptions}
                                        selectedOption={props.product.businessProduct?.size?.unit}
                                        onSelect={(option, name) => changeHandler(option, name!)}
                                        errors={props.errors}
                                    />
                                </Box>
                                <Box className={classes.barcodeCategoryContainer}>
                                    {props.product.businessProduct?.size &&
                                        !shouldHideAmount(props.product.businessProduct?.size?.unit) && (
                                            <NumberField2
                                                label="Amount"
                                                name="size.amount"
                                                value={
                                                    props.product.businessProduct?.size?.amount != null &&
                                                    props.product.businessProduct?.size?.amount !== ''
                                                        ? Number(props.product.businessProduct.size.amount)
                                                        : undefined
                                                }
                                                placeholder="0.00"
                                                onChange={changeHandler}
                                                endIcon={
                                                    <Typography>
                                                        {getUnit(props.product.businessProduct, masterData)}
                                                    </Typography>
                                                }
                                                scale={2}
                                            />
                                        )}
                                </Box>
                            </Box>
                            <Box className={classes.descriptionContainer}>
                                <DescriptionField2
                                    label="Product Description"
                                    name="description"
                                    value={props.product.businessProduct?.description || ''}
                                    onChange={changeHandler}
                                    maxCharacters={400}
                                    placeholder="Give a brief overview that describes your product’s best features. "
                                    className={classes.description}
                                />
                            </Box>
                            <Box className={classes.actions}>
                                <ButtonV3
                                    label="Next"
                                    onClick={props.onNext}
                                    size="large"
                                    variant="primary"
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
            {showLookup && (
                <LookupProduct
                    onSelect={selectProductHandler}
                    onClose={() => setShowLookup(false)}
                />
            )}
        </Box>
    );
};

export default Details;
